/**
 * TodaySessionChatCard — Nikki's ONE adaptive session, rendered inline in chat.
 *
 * Compact card mirroring the /workouts Today card for the chat thread. Start is
 * self-contained (creates a real session via the start endpoint, then routes
 * by entry mode: camera → /sessions/:id, timer → /timer/:id, guided → /mental).
 * If the user wants something else they just TYPE it — no swap button needed,
 * they're already in chat with Nikki.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Loader2, ChevronRight, CircleDot, Timer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import WorkoutMuscleMap, { musclesFor } from '../../WorkoutMuscleMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY = {
  strength_upper:    { label: 'Upper Strength', color: '#CCFF00' },
  strength_lower:    { label: 'Lower Strength', color: '#CCFF00' },
  strength_full:     { label: 'Full Body',      color: '#CCFF00' },
  hybrid:            { label: 'Hybrid',         color: '#FBBF24' },
  mobility:          { label: 'Mobility',       color: '#22D3EE' },
  yoga:              { label: 'Yoga',           color: '#A855F7' },
  breathwork:        { label: 'Breathwork',     color: '#60A5FA' },
  injury_management: { label: 'Injury Care',    color: '#F87171' },
  walk:              { label: 'Walk · Run',     color: '#34D399' },
};
const meta = (m) => MODALITY[m] || { label: m || 'Session', color: '#888' };

const TodaySessionChatCard = ({ data }) => {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const suggestion = data?.suggestion || {};
  const m = meta(suggestion.modality);

  const start = async () => {
    setStarting(true);
    try {
      const token = localStorage.getItem('hpme_token') || localStorage.getItem('token');
      const res = await axios.post(`${API}/workouts/today-suggestion/start`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { entry_mode, session_id } = res.data;
      if (entry_mode === 'timer') navigate(`/timer/${session_id}`);
      else if (entry_mode === 'guided') navigate('/mental');
      else navigate(`/sessions/${session_id}`);
    } catch (e) {
      toast.error('Could not start the session — try again?');
      setStarting(false);
    }
  };

  if (!suggestion.modality) {
    return (
      <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-4" data-testid="card-today-session-empty">
        <p className="text-sm text-zinc-400">I'm putting today's focus together…</p>
      </div>
    );
  }

  return (
    <div
      className="rounded-sm border bg-gradient-to-br from-zinc-900/60 to-zinc-900/20 p-4 space-y-3"
      style={{ borderColor: `${m.color}33` }}
      data-testid="card-today-session"
    >
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-[0.18em] font-secondary font-bold"
           style={{ color: m.color, backgroundColor: `${m.color}1A`, border: `1px solid ${m.color}33` }}
           data-testid="chat-today-modality">
        {m.label} · {suggestion.duration_min} min
      </div>
      <h4 className="font-secondary font-bold text-lg text-white leading-tight" data-testid="chat-today-title">
        {suggestion.title}
      </h4>
      {(suggestion.why || data?.why) && (
        <p className="text-xs text-zinc-300 leading-relaxed border-l-2 pl-3" style={{ borderColor: m.color }} data-testid="chat-today-why">
          {suggestion.why || data.why}
        </p>
      )}

      <div className="flex gap-3 items-start pt-1">
        {suggestion.exercises?.length > 0 && (
          <ul className="space-y-1 flex-1 min-w-0">
            {suggestion.exercises.slice(0, 6).map((ex, i) => {
              const trackable = ex.trackable === true;
              return (
                <li key={i} className="flex items-baseline gap-2 text-xs" data-testid={`chat-today-exercise-${i}`}>
                  <span className="text-zinc-600 tabular-nums w-4">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-zinc-200 flex-1">{ex.name}</span>
                  {ex.trackable !== undefined && (
                    <span
                      className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm text-[8px] uppercase tracking-wider ${
                        trackable
                          ? 'bg-primary/10 text-primary border border-primary/30'
                          : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                      }`}
                      title={trackable ? 'Reps auto-counted' : 'Follow along'}
                    >
                      {trackable ? <CircleDot className="w-2 h-2" /> : <Timer className="w-2 h-2" />}
                    </span>
                  )}
                  {ex.reps_or_seconds && <span className="text-zinc-500">{ex.reps_or_seconds}</span>}
                </li>
              );
            })}
          </ul>
        )}
        {/* Worked muscle groups — Nikki-driven body map */}
        <div className="h-24 w-16 shrink-0" data-testid="chat-today-muscle-map">
          <WorkoutMuscleMap
            primary={musclesFor(suggestion, suggestion.modality).primary}
            secondary={musclesFor(suggestion, suggestion.modality).secondary}
            size="sm"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={start}
          disabled={starting}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary text-black font-secondary font-bold py-2 rounded-sm disabled:opacity-50 text-xs uppercase tracking-wider"
          data-testid="chat-today-start-btn"
        >
          {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {starting ? 'Starting…' : 'Start session'}
        </button>
      </div>

      <button
        onClick={() => navigate('/workouts')}
        className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-primary pt-1"
        data-testid="chat-today-open-workouts"
      >
        Open Workouts <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
};

export default TodaySessionChatCard;
